import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';

interface HeaderContent {
  title?: string;
  subtitle?: string;
  actions?: ReactNode;
  leftContent?: ReactNode;
  rightContent?: ReactNode;
  /** When true, sticky chrome has no bottom border (still floats over content). */
  hideBorder?: boolean;
}

interface HeaderContextType {
  headerContent: HeaderContent;
  setHeaderContent: (content: HeaderContent) => void;
  clearHeaderContent: () => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const useHeader = () => {
  const context = useContext(HeaderContext);
  if (!context) {
    throw new Error('useHeader must be used within a HeaderProvider');
  }
  return context;
};

interface HeaderProviderProps {
  children: ReactNode;
}

export const HeaderProvider: React.FC<HeaderProviderProps> = ({ children }) => {
  const [headerContent, setHeaderContent] = useState<HeaderContent>({});

  const clearHeaderContent = useCallback(() => {
    setHeaderContent({});
  }, []);

  const value = useMemo(
    () => ({ headerContent, setHeaderContent, clearHeaderContent }),
    [headerContent, clearHeaderContent],
  );

  return (
    <HeaderContext.Provider value={value}>
      {children}
    </HeaderContext.Provider>
  );
};
